<template>
  <InterviewLayout>
    <template #header>
      <InterviewHeader :brand-image="brandImage" title="INTRO" />
    </template>

    <div class="interview-round-landing__body">
      <div class="interview-round-landing__container">
        <div
          v-if="titleImage"
          class="interview-round-landing__image"
          :style="{ backgroundImage: `url(${titleImage})` }"
          aria-hidden="true"
          data-test-id="RoundLanding_Image"
        />
        <h1 class="interview-round-landing__title">
          {{ title }}
        </h1>
      </div>
    </div>

    <template #footer>
      <InterviewFooter :is-loading="false" :submit-text="c('next')" @continue="$emit('start')" />
    </template>
  </InterviewLayout>

  <InterviewPageModal
    v-if="fsm.currentState === 'landing_videoModal' || fsm.currentState === 'landing_audioModal'"
  >
    <ModalLayout>
      <MediaWarning
        :media-type="
          (
            {
              landing_videoModal: 'video',
              landing_audioModal: 'audio',
            } as const
          )[fsm.currentState]
        "
        @continue="fsm.send('MODAL_CLOSE')"
        @abort="$emit('abort')"
      />
    </ModalLayout>
  </InterviewPageModal>
</template>

<script lang="ts">
import { useFSM } from '@fsmify/vue'
import { defineComponent, onBeforeMount, type PropType, reactive } from 'vue'

import { c } from '../../../intl'
import ModalLayout from '../../../shared/components/dumb/ModalLayout.vue'
import InterviewFooter from '../dumb/InterviewFooter.vue'
import InterviewHeader from '../dumb/InterviewHeader.vue'
import InterviewLayout from '../dumb/InterviewLayout.vue'
import InterviewPageModal from '../dumb/InterviewPageOverlay.vue'
import MediaWarning from '../dumb/MediaWarning.vue'

export default defineComponent({
  components: {
    InterviewLayout,
    InterviewFooter,
    InterviewHeader,
    InterviewPageModal,
    MediaWarning,
    ModalLayout,
  },
  props: {
    title: {
      required: true,
      type: String,
    },
    titleImage: {
      required: true,
      type: null as unknown as PropType<string | null>,
    },
    roundHasQuestionWithVideo: {
      required: true,
      type: Boolean,
    },
    roundHasQuestionWithAudio: {
      required: true,
      type: Boolean,
    },
    brandImage: {
      required: true,
      type: [String, null],
    },
  },
  emits: {
    start: () => true,
    abort: () => true,
  },
  setup(props) {
    const fsm = useFSM({
      initialState: 'landing',
      states: {
        landing: {
          OPEN_VIDEO_MODAL: 'landing_videoModal',
          OPEN_AUDIO_MODAL: 'landing_audioModal',
        },
        landing_videoModal: { MODAL_CLOSE: 'landing' },
        landing_audioModal: { MODAL_CLOSE: 'landing' },
      },
    })

    onBeforeMount(() => {
      if (props.roundHasQuestionWithVideo) {
        fsm.send('OPEN_VIDEO_MODAL')
        return
      }
      if (props.roundHasQuestionWithAudio) {
        fsm.send('OPEN_AUDIO_MODAL')
        return
      }
    })

    return {
      fsm: reactive(fsm),
      c,
    }
  },
})
</script>

<style lang="postcss">
.interview-round-landing {
  &__body {
    background-color: #f6f9fd;
    padding: 1rem;
  }

  &__container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #fff;
    border: 1px solid var(--color-blue-grey);
  }

  &__image {
    width: 80%;
    height: 36%;
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
    margin-bottom: 5em;
  }

  &__title {
    color: #036;
    font-size: var(--attest-font-size-l);
    text-align: center;
  }
}
</style>
